/**
 * File URL Retrieval Utility
 *
 * This utility provides a unified way to retrieve file URLs from either
 * Supabase Storage or Vercel Blob, based on the format of the file path.
 */

import { getFileFromStorage } from '@/db/storage/files'
import { getFileFromBlobStorage } from '@/db/storage/vercel-blob-files'

function isHttpUrl(value: string) {
  return value.startsWith('http://') || value.startsWith('https://')
}

function readVercelBlobPath(value: string) {
  try {
    const parsed = JSON.parse(value) as unknown

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }

    const record = parsed as { pathname?: unknown; url?: unknown }

    return typeof record.pathname === 'string' || typeof record.url === 'string'
      ? record
      : null
  } catch {
    return null
  }
}

/**
 * Get a file URL from either Supabase Storage or Vercel Blob
 *
 * @param filePath The file path or blob data string
 * @returns The URL for accessing the file
 */
export const getFileUrl = async (filePath: string): Promise<string> => {
  const normalizedPath = filePath.trim()

  if (!normalizedPath) {
    throw new Error('File path is missing')
  }

  if (isHttpUrl(normalizedPath)) {
    return normalizedPath
  }

  const vercelBlobPath = readVercelBlobPath(normalizedPath)
  if (vercelBlobPath) {
    if (typeof vercelBlobPath.url === 'string' && isHttpUrl(vercelBlobPath.url)) {
      return vercelBlobPath.url
    }

    try {
      return await getFileFromBlobStorage(normalizedPath)
    } catch (error) {
      console.error('Error getting file from Vercel Blob:', error)
      throw new Error('Failed to retrieve file URL')
    }
  }

  // Otherwise treat as Supabase Storage path
  try {
    return await getFileFromStorage(normalizedPath)
  } catch (error) {
    console.error('Error getting file from Supabase Storage:', error)
    throw new Error('Failed to retrieve file URL')
  }
}

/**
 * Determine if a file path is for a Vercel Blob
 *
 * @param filePath The file path to check
 * @returns True if the path is for a Vercel Blob
 */
export const isVercelBlobPath = (filePath: string): boolean => {
  return Boolean(readVercelBlobPath(filePath))
}
